import { Fragment, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useCurrentUser } from '~/lib/auth/use-current-user'
import type {
  DayCell as DayCellPayload,
  OooCell,
  RangeSnapshot,
  RangeUser,
} from '~/lib/api/types'
import { toIsoDate } from '~/lib/time/format'
import { DayCell } from './day-cell'
import { OooBand } from './ooo-band'
import styles from './week-grid.module.scss'

const WEEKDAY_FMT = new Intl.DateTimeFormat('en-US', { weekday: 'short' })
const DAY_NUM_FMT = new Intl.DateTimeFormat('en-US', { day: 'numeric' })

type Props = {
  data: RangeSnapshot
  onCellClick: (checkinId: string) => void
}

export function WeekGrid({ data, onCellClick }: Props) {
  const todayIso = toIsoDate(new Date())
  const { data: me } = useCurrentUser()
  const isAdmin = me?.user.is_admin ?? false

  const dayHeaders = useMemo(
    () =>
      data.days.map(d => ({
        date: d.date,
        weekday: WEEKDAY_FMT.format(parseLocal(d.date)),
        dayNum: DAY_NUM_FMT.format(parseLocal(d.date)),
        isToday: d.date === todayIso,
        holiday: d.holiday,
      })),
    [data.days, todayIso]
  )

  const holidayDates = useMemo(
    () => new Set(data.days.filter(d => d.holiday).map(d => d.date)),
    [data.days]
  )

  return (
    <div
      className={styles.grid}
      role="grid"
      aria-label="Calendar week grid"
      style={
        {
          '--day-cols': data.days.length,
        } as React.CSSProperties
      }
    >
      {/* Header row: empty corner + day labels */}
      <div className={`${styles.headerRow} ${styles.cornerCell}`} aria-hidden />
      {dayHeaders.map(h => (
        <div
          key={h.date}
          className={`${styles.headerRow} ${styles.headerCell} ${h.isToday ? styles.todayCol : ''} ${h.holiday ? styles.holidayHeader : ''}`}
          role="columnheader"
        >
          <span className={styles.weekdayLabel}>{h.weekday}</span>
          <span
            className={`${styles.dayNum} mono ${h.isToday ? styles.dayNumToday : ''}`}
          >
            {h.dayNum}
          </span>
          {h.holiday && (
            <span className={styles.holidayLine}>
              <span className={styles.holidayShape} aria-hidden>
                ◇
              </span>
              <span className={styles.holidayName}>{h.holiday.name}</span>
            </span>
          )}
        </div>
      ))}

      {/* Engineer rows: each user emits 1 sticky engineer cell + a flat list
          of row entries (single cells, OOO bands, holiday gaps) as direct
          grid children inside a Fragment for keying. */}
      {data.users.map(user => (
        <Fragment key={user.id}>
          <div className={`${styles.engineerCell}`} role="rowheader">
            {isAdmin ? (
              <Link
                to={`/people/${user.slack_user_id}`}
                className={styles.engineerName}
              >
                {user.display_name ?? user.slack_user_id}
              </Link>
            ) : (
              <span className={styles.engineerName}>
                {user.display_name ?? user.slack_user_id}
              </span>
            )}
            <span className={styles.engineerTz}>{shortTz(user.timezone)}</span>
          </div>
          {buildRowEntries(user, data).map(entry => {
            if (entry.kind === 'ooo_band') {
              return (
                <OooBand
                  key={`${user.id}-ooo-${entry.cell.time_off_id}-${entry.firstDate}`}
                  cell={entry.cell}
                  span={entry.spanCount}
                />
              )
            }
            const isTodayCol = entry.date === todayIso
            const isHolidayCol = holidayDates.has(entry.date)
            const wrapClass = `${styles.dayCellWrap} ${isTodayCol ? styles.todayCol : ''} ${isHolidayCol ? styles.holidayCol : ''}`
            if (entry.kind === 'empty') {
              return (
                <div
                  key={`${user.id}-empty-${entry.date}`}
                  className={wrapClass}
                  aria-hidden
                />
              )
            }
            const checkinId =
              entry.cell.kind === 'working' ||
              entry.cell.kind === 'parse_failed'
                ? entry.cell.checkin_id
                : null
            if (checkinId) {
              return (
                <button
                  type="button"
                  key={`${user.id}-${entry.date}`}
                  className={`${wrapClass} ${styles.clickable}`}
                  onClick={() => onCellClick(checkinId)}
                  aria-label={`Open detail for ${user.display_name ?? user.slack_user_id} on ${entry.date}`}
                >
                  <DayCell
                    cell={entry.cell}
                    date={entry.date}
                    todayIso={todayIso}
                  />
                </button>
              )
            }
            return (
              <div
                key={`${user.id}-${entry.date}`}
                className={wrapClass}
                role="gridcell"
              >
                <DayCell
                  cell={entry.cell}
                  date={entry.date}
                  todayIso={todayIso}
                />
              </div>
            )
          })}
        </Fragment>
      ))}
    </div>
  )
}

type RowEntry =
  | { kind: 'cell'; date: string; cell: DayCellPayload }
  | { kind: 'empty'; date: string }
  | { kind: 'ooo_band'; cell: OooCell; firstDate: string; spanCount: number }

function buildRowEntries(user: RangeUser, data: RangeSnapshot): RowEntry[] {
  const entries: RowEntry[] = []
  let i = 0
  while (i < data.days.length) {
    const day = data.days[i]
    if (!day) {
      i += 1
    } else {
      const cell = day.cells.find(c => c.user_id === user.id)
      if (!cell) {
        entries.push({ kind: 'empty', date: day.date })
        i += 1
      } else if (cell.kind === 'ooo') {
        let j = i + 1
        while (j < data.days.length) {
          const nextDay = data.days[j]
          if (!nextDay) break
          const nextCell = nextDay.cells.find(c => c.user_id === user.id)
          if (
            nextCell?.kind !== 'ooo' ||
            nextCell.time_off_id !== cell.time_off_id
          )
            break
          j += 1
        }
        entries.push({
          kind: 'ooo_band',
          cell,
          firstDate: day.date,
          spanCount: j - i,
        })
        i = j
      } else {
        entries.push({ kind: 'cell', date: day.date, cell })
        i += 1
      }
    }
  }
  return entries
}

function parseLocal(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y as number, (m as number) - 1, d as number)
}

function shortTz(tz: string): string {
  // "America/Los_Angeles" -> "Los Angeles"
  const parts = tz.split('/')
  const last = parts[parts.length - 1] ?? tz
  return last.replace(/_/g, ' ')
}
